import type { ReactNode } from 'react'
import type { SipIdealPlanRecommendationApiResponse } from '../../api/idealRecommendation.types'
import {
  ConsistencyScorePointer,
  PowerRankPointer,
  RecencyScorePointer,
  VolatilityScorePointer
} from '../../../../plans-ui-components/PlanRecommendationScoreReasonPointers'
import { NumberedItem } from '../../../../../../../components/ui-components/NumberedItem'
import { buildPlanAllocationRangeLabel } from '../../../../display-values/lumpsumPlanPercentage'
import { formatSipCategorySipsLabel } from '../../../../utils/sip/planListUtils'
import { collectIdealPlanScoreOrRankPointerKinds } from '../../../../lumpsum/plan-bottom-sheet/ideal/body/IdealPlanReasonPointers'

type IdealSipPlanReasonsToInvestProps = {
  planDetails: SipIdealPlanRecommendationApiResponse | null
}

type IdealSipPlanReasonPointerKind =
  | 'MAINTAIN_EXPOSURE'
  | 'POWER_RANK'
  | 'CONSISTENCY_SCORE'
  | 'RECENCY_SCORE'
  | 'VOLATILITY_SCORE'

type IdealSipPlanPointerWrapperProps = {
  index: number
  borderBottom?: boolean
}

export function IdealSipPlanReasonsToInvest({ planDetails }: IdealSipPlanReasonsToInvestProps) {
  const pointers = buildIdealSipPlanReasonPointers(planDetails)

  if (pointers.length === 0) {
    return null
  }

  return (
    <div className="pt-6">
      <div className="px-5 pb-2 text-[18px] font-[500] leading-[130%] text-[#232323]">
        Reasons to continue this SIP
      </div>
      {pointers}
    </div>
  )
}

function buildIdealSipPlanReasonPointers(
  planDetails: SipIdealPlanRecommendationApiResponse | null
): ReactNode[] {
  if (!planDetails) {
    return []
  }

  const categorySipsLabel = formatSipCategorySipsLabel(planDetails?.categoryName)
  const categoryAllocationRangeLabel = buildPlanAllocationRangeLabel({ idealAllocation: 
    planDetails?.categoryIdealAllocation
   })
  const pointerKinds: IdealSipPlanReasonPointerKind[] = []

  if (categorySipsLabel.length > 0 && categoryAllocationRangeLabel.length > 0) {
    pointerKinds.push('MAINTAIN_EXPOSURE')
  }
  pointerKinds.push(...collectIdealPlanScoreOrRankPointerKinds(planDetails))

  return pointerKinds.map((pointerKind, pointerIndex) => {
    const pointerNumber = pointerIndex + 1
    const borderBottom = pointerIndex < pointerKinds.length - 1

    return renderIdealSipPlanReasonPointer({
      pointerKind,
      index: pointerNumber,
      borderBottom,
      planDetails,
      categorySipsLabel,
      categoryAllocationRangeLabel
    })
  })
}

function renderIdealSipPlanReasonPointer({
  pointerKind,
  index,
  borderBottom,
  planDetails,
  categorySipsLabel,
  categoryAllocationRangeLabel
}: {
  pointerKind: IdealSipPlanReasonPointerKind
  index: number
  borderBottom: boolean
  planDetails: SipIdealPlanRecommendationApiResponse
  categorySipsLabel: string
  categoryAllocationRangeLabel: string
}): ReactNode {
  if (pointerKind === 'MAINTAIN_EXPOSURE') {
    return (
      <MaintainSipExposurePointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categorySipsLabel={categorySipsLabel}
        categoryAllocationRangeLabel={categoryAllocationRangeLabel}
      />
    )
  }

  if (pointerKind === 'POWER_RANK') {
    return (
      <PowerRankPointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categoryLabel={categorySipsLabel}
        powerRank={planDetails?.powerRank ?? null}
        powerRating={planDetails?.powerRating ?? ''}
        totalCategoryCount={planDetails?.totalCategoryCount ?? null}
      />
    )
  }

  const scoringData = planDetails?.scoringData
  const maxScore = scoringData?.maxScore ?? 0

  if (pointerKind === 'CONSISTENCY_SCORE') {
    return (
      <ConsistencyScorePointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categoryLabel={categorySipsLabel}
        score={scoringData?.consistencyScore}
        maxScore={maxScore}
        performanceTag={scoringData?.consistencyScorePerformanceTag ?? ''}
      />
    )
  }

  if (pointerKind === 'RECENCY_SCORE') {
    return (
      <RecencyScorePointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categoryLabel={categorySipsLabel}
        score={scoringData?.recencyScore}
        maxScore={maxScore}
        performanceTag={scoringData?.recencyScorePerformanceTag ?? ''}
      />
    )
  }

  return (
    <VolatilityScorePointer
      key={pointerKind}
      index={index}
      borderBottom={borderBottom}
      categoryLabel={categorySipsLabel}
      score={scoringData?.volatilityScore}
      maxScore={maxScore}
      performanceTag={scoringData?.volatilityScorePerformanceTag ?? ''}
    />
  )
}

export function MaintainSipExposurePointer({
  index,
  borderBottom,
  categorySipsLabel,
  categoryAllocationRangeLabel
}: IdealSipPlanPointerWrapperProps & {
  categorySipsLabel: string
  categoryAllocationRangeLabel: string
}) {
  return (
    <NumberedItem
      number={index}
      title={`Helps maintain ${categorySipsLabel} exposure`}
      subtitle={`within ideal range of ${categoryAllocationRangeLabel}, as per allocation strategy`}
      borderBottom={borderBottom}
    >
      {null}
    </NumberedItem>
  )
}
